import { MAIL_SNOOZE_MAX_AHEAD_MS } from '@embertime/shared'

// Snooze presets are Berlin wall-clock times, whatever zone the browser runs
// in: a mail snoozed "bis morgen früh" must come back at 08:00 in Germany.
const ZONE = 'Europe/Berlin'
const WEEKDAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.']
/** A custom time must lie at least this far ahead: the POST only leaves after
 * the 5 s undo window, and the server checks "future" against its own clock. */
const MIN_LEAD_MS = 60_000

const partsFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h23',
})

export interface BerlinParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  /** 0 = Sunday … 6 = Saturday */
  weekday: number
}

export function berlinParts(date: Date): BerlinParts {
  const p: Record<string, number> = {}
  for (const part of partsFormat.formatToParts(date)) {
    if (part.type !== 'literal') p[part.type] = Number(part.value)
  }
  const { year = 0, month = 1, day = 1, hour = 0, minute = 0 } = p
  return {
    year,
    month,
    day,
    hour,
    minute,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  }
}

function offsetMs(ms: number) {
  const p = berlinParts(new Date(ms))
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
  return wall - Math.floor(ms / 60_000) * 60_000
}

/** Berlin wall time → instant. The offset is probed twice so a time right
 * after a DST switch picks the offset valid at that time, not at the guess. */
export function berlinWallTime(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const first = guess - offsetMs(guess)
  const second = guess - offsetMs(first)
  return new Date(second)
}

/** Calendar arithmetic on the Berlin date, independent of offsets. */
function addDays(p: BerlinParts, days: number) {
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + days))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }
}

const pad = (n: number) => String(n).padStart(2, '0')
const time = (p: BerlinParts) => `${pad(p.hour)}:${pad(p.minute)}`
const weekdayLabel = (d: Date) => {
  const p = berlinParts(d)
  return `${WEEKDAYS[p.weekday]} ${time(p)}`
}
const datedLabel = (d: Date, withYear = false) => {
  const p = berlinParts(d)
  return `${WEEKDAYS[p.weekday]} ${pad(p.day)}.${pad(p.month)}.${withYear ? p.year : ''} ${time(p)}`
}

export type SnoozePresetId = 'tonight' | 'tomorrow' | 'weekend' | 'nextWeek'
export interface SnoozePreset {
  id: SnoozePresetId
  title: string
  until: Date
  label: string
  disabled: boolean
}

export function snoozePresets(now: Date): SnoozePreset[] {
  const today = berlinParts(now)
  const at = (days: number, hour: number) => {
    const d = addDays(today, days)
    return berlinWallTime(d.year, d.month, d.day, hour, 0)
  }
  const tonight = at(0, 18)
  const tomorrow = at(1, 8)
  // Saturday before 09:00 still catches this weekend; otherwise the next Saturday.
  const weekend =
    today.weekday === 6 && today.hour < 9 ? at(0, 9) : at((6 - today.weekday + 7) % 7 || 7, 9)
  // "Nächste Woche" on a Monday means the Monday after, never today.
  const nextWeek = at((1 - today.weekday + 7) % 7 || 7, 8)
  return [
    {
      id: 'tonight',
      title: 'Heute Abend',
      until: tonight,
      label: weekdayLabel(tonight),
      // One hour of lead: a snooze of minutes is not worth the IMAP round trip.
      disabled: today.hour >= 17,
    },
    {
      id: 'tomorrow',
      title: 'Morgen früh',
      until: tomorrow,
      label: weekdayLabel(tomorrow),
      disabled: false,
    },
    {
      id: 'weekend',
      title: 'Wochenende',
      until: weekend,
      label: weekdayLabel(weekend),
      disabled: false,
    },
    {
      id: 'nextWeek',
      title: 'Nächste Woche',
      until: nextWeek,
      label: datedLabel(nextWeek),
      disabled: false,
    },
  ]
}

export type SnoozeParseError = 'past' | 'too_far' | 'invalid'

/** Reads an `<input type="datetime-local">` value as Berlin wall time. */
export function parseCustom(
  value: string,
  now: Date,
): { ok: true; until: Date } | { ok: false; error: SnoozeParseError } {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,3})?)?$/.exec(value)
  if (!m) return { ok: false, error: 'invalid' }
  const [year, month, day, hour, minute] = m.slice(1, 6).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ]
  const calendar = new Date(Date.UTC(year, month - 1, day))
  if (
    calendar.getUTCMonth() !== month - 1 ||
    calendar.getUTCDate() !== day ||
    hour > 23 ||
    minute > 59
  ) {
    return { ok: false, error: 'invalid' }
  }
  const until = berlinWallTime(year, month, day, hour, minute)
  const ahead = until.getTime() - now.getTime()
  if (ahead < MIN_LEAD_MS) return { ok: false, error: 'past' }
  if (ahead > MAIL_SNOOZE_MAX_AHEAD_MS) return { ok: false, error: 'too_far' }
  return { ok: true, until }
}

/** Instant → `datetime-local` value in Berlin wall time (for value/min/max). */
export function toDatetimeLocal(date: Date): string {
  const p = berlinParts(date)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${time(p)}`
}

const dayNumber = (p: { year: number; month: number; day: number }) =>
  Date.UTC(p.year, p.month - 1, p.day) / 86_400_000

/** Short return time for the undo toast: "heute 18:00", "morgen 08:00",
 * weekday within the current week, else with date (and year if it differs). */
export function formatSnoozeLabel(date: Date, now: Date): string {
  const target = berlinParts(date)
  const today = berlinParts(now)
  const diff = dayNumber(target) - dayNumber(today)
  if (diff === 0) return `heute ${time(target)}`
  if (diff === 1) return `morgen ${time(target)}`
  // Monday-based week: days until Sunday of the current week.
  const leftInWeek = (7 - today.weekday) % 7
  if (diff > 1 && diff <= leftInWeek) return weekdayLabel(date)
  return datedLabel(date, target.year !== today.year)
}
